import { useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  TimeXAxis,
  YAxis,
  Box,
} from '@cast/design-system';
import { dashesOrNum, isE2E } from '@cast/utils';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { HiddenDot } from 'components/chart';
import { useReactRouterStrategy } from 'components/chart/hooks';
import { attachTimeTicks } from 'utils/chart';

import { JvmChartDataPoint, JvmMetricKey, jvmMetricKeys } from './_constants';
import {
  RIGHTSIZING_QUERY_PARAM_KEYS,
  useRightsizingTimeRangeSelection,
} from '../../../_hooks/useRightsizingTimeRangeSelection';
import { jvmMetricToColor, jvmMetricToTitle } from '../constants';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

type JvmHeapChartProps = {
  data: JvmChartDataPoint[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
  isMetricDisabled: (metric: JvmMetricKey) => boolean;
  timeZone: string;
};

const useJvmChartZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: RIGHTSIZING_QUERY_PARAM_KEYS,
});

export const JvmHeapChart = ({
  data,
  isLoading,
  hasError,
  refetch,
  isMetricDisabled,
  timeZone,
}: JvmHeapChartProps) => {
  const { cluster } = useCluster();
  const { getRange } = useRightsizingTimeRangeSelection();
  const chartData = useMemo(() => attachTimeTicks(data), [data]);

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'JVM heap usage',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  return (
    <Box height="100%" position="relative">
      {!hasError && (
        <ChartContainer
          data={chartData}
          isLoading={isLoading}
          sx={{ width: '100%' }}
          chartActionsSx={{ top: 0, right: 0 }}
          noDataOverlayProps={{
            title: 'No metrics found',
            body: undefined,
          }}
          zoomProps={{
            xAxisDataKey: 'chartTimestampUnix',
            useZoomState: useJvmChartZoomState,
          }}
          testId="jvm-chart"
          syncId="workload-metrics"
          analyzeWithOpsPilot={analyzeWithOpsPilot}
        >
          <ChartGrid hideVertical />

          {jvmMetricKeys.map((dataKey) => {
            if (isMetricDisabled(dataKey)) return null;

            return (
              <Line<JvmChartDataPoint>
                key={dataKey}
                dataKey={dataKey}
                stroke={jvmMetricToColor[dataKey]}
                strokeWidth={1.5}
                tooltipConfig={{
                  displayName: jvmMetricToTitle[dataKey],
                  unit: 'GiB',
                }}
                dot={isE2E() ? HiddenDot : false}
              />
            );
          })}

          <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />

          <YAxis allowDecimals unitFormatter={(v) => `${v} GiB`} />

          <ChartTooltip<JvmChartDataPoint>
            header={(props) => (
              <ChartTooltipTimestampHeader {...props} timezone={timeZone} />
            )}
            formatValue={(value) => dashesOrNum({ value, roundBy: 3 })}
            testId="data-popper"
          />
        </ChartContainer>
      )}
      {hasError && <FailedToLoad refresh={refetch} />}
    </Box>
  );
};
